import { useGetMemberLectureData } from '#api/member';
import {
  useGetScheduleCoachGroup,
  usePatchScheduleCoachGroup,
  usePostScheduleCoach,
  usePostScheduleCoachGroup
} from '#api/schedules';
import { Text } from '#atoms';
import { TIME_LIST } from '#const/date';
import { PATH } from '#const/path';
import { useLectureSearchQuery } from '#hooks/queries/lecture/useLectureSearchQuery';
import queryKeys from '#hooks/queries/queryKeys';
import { usePatchScheduleCoachMutation } from '#hooks/queries/schedules/usePatchScheduleCoachMutation';
import { useScheduleCoachDetailQuery } from '#hooks/queries/schedules/useScheduleCoachDetailQuery';
import { LabelWithButton } from '#molecules';
import { useScheduleManage } from '#providers/SchedulesManage';
import { useToast } from '#providers/ToastProvider';
import { useUserInfo } from '#providers/UserInfo';
import CoachMark from '#shared/CoachMark';
import { PayloadLecture } from '#types/schedule';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState
} from 'react';
import Dropdown, { Option } from 'react-dropdown';
import 'react-dropdown/style.css';
import TagManager from 'react-gtm-module';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { FibudCalendarV2 } from 'src/components/molecules/FibudCalendarV2';
import { LabelButton } from 'src/components/molecules/LabelWithButton/styles';
import Loading from 'src/components/molecules/Loading';
import { RoundStatus } from '..';
import { getTime } from '../utils';
import { GroupRoundInfo } from './GroupRoundInfo';
import Membership from './Membership';
import { Repitition } from './Repetition';
import { RoundInfo } from './RoundInfo';
import * as S from './styles';
import useScheduleMembership from './useScheduleMembership';

dayjs.locale('ko');

export interface LectureScheduleHandle {
  handleSave: () => void;
}

const LectureScheduleContents = forwardRef<LectureScheduleHandle>(
  (props, ref) => {
    const { showToast } = useToast();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [searchParams] = useSearchParams();
    const lectureScheduleId = decodeURIComponent(
      searchParams.get('lecture-schedule-id') ?? ''
    );
    const preTaughtAt = decodeURIComponent(
      searchParams.get('pre-tauthAt') ?? ''
    );

    // 그룹 수업 수정 api
    const { mutate: patchScheduleCoachGroup } = usePatchScheduleCoachGroup({
      onSuccess: () => {
        showToast({
          type: 'success',
          message: `일정 ${isModify ? '수정' : '등록'} 성공했습니다.`
        });
        clearSelectedLecture();
        clearGroupMembers();
        navigate(-1);
      },
      onError: () => {
        showToast({
          type: 'fail',
          message: '그룹 수업 일정 수정에 실패했습니다.'
        });
      }
    });

    const defaultDate = decodeURIComponent(searchParams.get('date') ?? '');

    const {
      selectedLecture,
      setSelectedLecture,
      clearSelectedLecture,
      groupMembers,
      setGroupMembers,
      clearGroupMembers
    } = useScheduleManage();

    // 일반 수업: lectureScheduleId, 그룹 수업: preTaughtAt
    const isModify = !!lectureScheduleId;
    const isGroup = selectedLecture.isGroup || groupMembers.length > 0;
    const isGroupModify = !!preTaughtAt;

    // 안 쓰는 코드 주석 처리
    const { data: nextLectureRound } = useGetMemberLectureData({
      personId: selectedLecture.personId,
      lectureId: selectedLecture.lectureId,
      disable: !!lectureScheduleId
    });
    const { userInfo } = useUserInfo();
    const { data: schedule, isLoading } = useScheduleCoachDetailQuery({
      id: lectureScheduleId as string
    });

    // 안 쓰는 코드 주석 처리
    // 수정 가능 여부
    const isModifyPossible =
      (isGroupModify || isModify) &&
      (schedule?.isFirstRoundAndNotMoreRegisted || false);

    // 수정 조건 모달 표시 여부
    // 안 쓰는 코드 주석 처리
    const [isModifyConditionModalOpen, setIsModifyConditionModalOpen] =
      useState<boolean>(isModifyPossible);

    const { data: groupCoachSchedule } = useGetScheduleCoachGroup(
      {
        lectureId: schedule?.lectureId || '',
        taughtAt: schedule?.taughtAt || ''
      },
      {
        enabled:
          schedule?.isGroup === true &&
          !!schedule?.lectureId &&
          !!schedule?.taughtAt
      }
    );

    // 안 쓰는 코드 주석 처리
    const { membership } = useScheduleMembership();

    // 반복이 수정 가능한 경우
    const isRoundModify = schedule?.isFirstRoundAndNotMoreRegisted;

    const { data: lectureList } = useLectureSearchQuery(userInfo?.id);
    const { mutateAsync: patchScheduleCoach, isLoading: isPatchLoading } =
      usePatchScheduleCoachMutation();

    // 안 쓰는 코드 주석 처리
    // 그룹 수업 등록
    const { mutate: postScheduleCoachGroup } = usePostScheduleCoachGroup({
      onSuccess: (res: {
        success: PayloadLecture[];
        failure: PayloadLecture[];
      }) => {
        if (res.failure.length > 0) {
          showToast({
            type: 'fail',
            message: '그룹 수업 일정 등록에 실패했습니다.'
          });
        } else {
          showToast({
            type: 'success',
            message: `일정 ${isModify ? '수정' : '등록'} 성공했습니다.`
          });
          clearSelectedLecture();
          clearGroupMembers();
          TagManager.dataLayer({
            dataLayer: {
              event: 'calendertab_plan_saved',
              category: '일정 관리',
              action: 'calendertab_plan_saved',
              plan_id: res.success[0].lectureId,
              value: 1
            }
          });
          navigate(-1);
        }
      },
      onError: (e: any) => {
        showToast({
          type: 'fail',
          message:
            e?.response?.data?.message ?? '그룹 수업 일정 등록에 실패했습니다.'
        });
      }
    });

    // mutate: postScheduleCoach 있었음
    const { isLoading: isPostLoading, mutate: postScheduleCoachMutate } =
      usePostScheduleCoach({
        onSuccess: data => {
          queryClient.invalidateQueries([queryKeys.SCHEDULE]);
          showToast({
            type: 'success',
            message: `일정 ${isModify ? '수정' : '등록'} 성공했습니다.`
          });
          clearSelectedLecture();
          TagManager.dataLayer({
            dataLayer: {
              event: 'calendertab_plan_saved',
              category: '일정 관리',
              action: 'calendertab_plan_saved',
              plan_id: data,
              value: 1
            }
          });
          navigate(-1);
        },
        onError: (e: any) => {
          showToast({
            type: 'fail',
            message: e?.response?.data?.message ?? '일정 등록 실패했습니다.'
          });
        }
      });

    // 안 쓰는 코드 주석 처리
    const [totalRoundLength, setTotalRoundLength] = useState<number>(100);

    // setRoundStatus 있었음
    const [roundStatus] = useState<RoundStatus>();
    const [isCalenderOpen, setIsCalenderOpen] = useState<boolean>(false);

    const startTimeOptions = useMemo(() => {
      return [...TIME_LIST].slice(0, -1);
    }, []);

    const endTimeOptions = useMemo(() => {
      const [startHour, startMin] = getTime(selectedLecture.startTime);
      const [endHour, endedMin] = getTime(selectedLecture.endedTime);

      const index = TIME_LIST.findIndex(
        time => time === selectedLecture.startTime
      );
      const options = [...TIME_LIST].splice(index + 1);

      if (
        new Date(2024, 7, 1, startHour, startMin) >=
        new Date(2024, 7, 1, endHour, endedMin)
      ) {
        setSelectedLecture({ endedTime: options.at(1) ?? options.at(0) });
      }

      return options;
    }, [selectedLecture.startTime]);
    // 안 쓰는 코드 주석 처리
    const totalRoundOptions = useMemo<string[]>(() => {
      return Array.from({ length: totalRoundLength || 100 }, (_, i) =>
        String(i + 1)
      );
    }, [totalRoundLength]);
    // 안 쓰는 코드 주석 처리
    const roundOptions = useMemo<string[]>(() => {
      return Array.from({ length: selectedLecture.totalRound || 100 }, (_, i) =>
        String(i + 1)
      );
    }, [selectedLecture.totalRound]);

    /** 값이 없는 경우 post가 되지 않도록 validation */
    const isPossiblePostSendingLecture = (
      sendingLecture: PayloadLecture
    ): boolean => {
      if (
        sendingLecture.personId === '' ||
        sendingLecture.lectureId === '' ||
        sendingLecture.totalRound === 0 ||
        sendingLecture.taughtAt === '' ||
        sendingLecture.endedAt === '' ||
        sendingLecture.round === 0
        // sendingLecture.scheduleMemo === ''
      ) {
        return false;
      }

      return true;
    };

    const handleChangeStartTime = (option: Option) => {
      setSelectedLecture({
        startTime: option.value
      });
    };

    const handleChangeEndTime = (option: Option) => {
      setSelectedLecture({
        endedTime: option.value
      });
    };

    const handleTotalRound = (value: string | number) => {
      const _round =
        Number(value) < selectedLecture.round ? 1 : selectedLecture.round;

      setSelectedLecture({
        totalRound: Number(value),
        round: _round
      });
    };

    const handleSave = async () => {
      const [startHour, startMin] = getTime(selectedLecture.startTime);
      const [endedHour, endedMin] = getTime(selectedLecture.endedTime);

      const taughtAt = dayjs(selectedLecture.activeDate)
        .set('hour', startHour)
        .set('minute', startMin);
      const endedAt = dayjs(selectedLecture.activeDate)
        .set('hour', endedHour)
        .set('minute', endedMin);

      const payload: PayloadLecture = {
        personId: selectedLecture.personId,
        lectureId: selectedLecture.lectureId,
        round: selectedLecture.round,
        totalRound: selectedLecture.totalRound,
        taughtAt: taughtAt.format('YYYY-MM-DD HH:mm'),
        endedAt: endedAt.format('YYYY-MM-DD HH:mm'),
        scheduleMemo: selectedLecture.memo,
        repetition: selectedLecture.repetition
      };

      if (!isGroup && !isPossiblePostSendingLecture(payload)) {
        showToast({
          type: 'fail',
          message: '입력되지 않은 항목이 있습니다.'
        });
        return;
      }

      // 그룹 수업일 경우 api 보낼 파라미터 생성
      const groupLecture = groupMembers.map(member => ({
        personId: member.personId,
        lectureId: selectedLecture.lectureId,
        round: member.round,
        totalRound: member.totalRound,
        taughtAt: taughtAt
          .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
          .replace(/:\d{2}\.\d{3}/, ':00.000'),
        endedAt: endedAt
          .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
          .replace(/:\d{2}\.\d{3}/, ':00.000'),
        scheduleMemo: selectedLecture.memo,
        repetition: undefined
      }));

      if (
        isGroup &&
        groupLecture.every(lecture => !isPossiblePostSendingLecture(lecture))
      ) {
        showToast({
          type: 'fail',
          message: '입력되지 않은 항목이 있습니다.'
        });
        return;
      }

      try {
        if (isGroup) {
          if (isModify) {
            if (!groupCoachSchedule || !schedule) return;

            // groupMember에 있는 회원들로만
            const filteredGroupCoachSchedule = groupCoachSchedule.filter(item =>
              groupMembers.find(member => member.personId === item.personId)
            );
            patchScheduleCoachGroup({
              lectureId: schedule.lectureId || '',
              payload: {
                preTaughtAt: schedule.taughtAt,
                newCoachSchedules: filteredGroupCoachSchedule.map(item => {
                  return {
                    personId: item.personId,
                    taughtAt: item.taughtAt,
                    endedAt: item.endedAt,
                    lectureId: item.lectureId,
                    round: item.round,
                    totalRound: item.totalRound
                  };
                })
              }
            });
          } else {
            // 안 쓰는 코드 주석 처리
            postScheduleCoachGroup({
              lectureId: selectedLecture.lectureId as string,
              payload: groupLecture
            });
            // 이전에 주석 처리 되어있음. (11/18)
            // TagManager.dataLayer({
            //   dataLayer: {
            //     event: 'calendertab_plan_saved',
            //     category: '일정 관리',
            //     action: 'calendertab_plan_saved',
            //     value: 1
            //   }});
          }
        } else {
          if (isModify) {
            if (isPatchLoading || !payload.repetition) return;

            // 회차를 변경하지 않은 경우
            const isRoundNotChanged =
              schedule?.round === payload.round &&
              schedule?.totalRound === payload.totalRound;

            // 반복을 수정하지 않은 경우
            const isRepetitionNotChanged =
              payload.repetition.daysOfWeek.length === 0;

            // 백엔드에서 날짜 외에는 체크하지 않음
            // 따라서 변경하지 않은 회차, 반복은 null로 보내줌
            await patchScheduleCoach({
              id: lectureScheduleId as string,
              taughtAt: payload.taughtAt,
              endedAt: payload.endedAt,
              round: isRoundNotChanged ? null : payload.round,
              totalRound: isRoundNotChanged ? null : payload.totalRound,
              repetition: isRepetitionNotChanged ? null : payload.repetition
            });
          } else {
            if (isPostLoading) return;

            // 안 쓰는 코드 주석 처리
            const newPayload = {
              ...payload,
              lectureId: membership?.lectureId || ''
            };

            // 안 쓰는 코드 주석 처리
            // postScheduleCoach(newPayload);
            postScheduleCoachMutate(newPayload);
          }
        }
      } catch (e: any) {
        showToast({
          type: 'fail',
          message: e?.response?.data?.message ?? '일정 등록 실패했습니다.'
        });
      }
    };

    useEffect(() => {
      if (defaultDate) {
        const activeDate = dayjs(defaultDate);
        const endedAt = dayjs(activeDate).add(1, 'hour');

        setSelectedLecture({
          activeDate,
          startTime: activeDate.format('HH:mm'),
          endedTime: endedAt.format('HH:mm')
        });
      }
    }, [defaultDate]);

    useEffect(() => {
      if (!groupCoachSchedule) return;

      setGroupMembers(
        groupCoachSchedule.map(item => ({
          personId: item.personId,
          personName: item.personName,
          round: item.round,
          totalRound: item.totalRound
        }))
      );
    }, []);

    // // 처음 작성하는 경우 수업별 회차 정보 가져와서 세팅하기
    // useEffect(() => {
    //   if (isModify) {
    //     return;
    //   }

    //   const newState: Record<string, number> = {
    //     round: 0,
    //     totalRound: 0
    //   };

    //   if (nextLectureRound?.lectureId && nextLectureRound?.personId) {
    //     newState['round'] = nextLectureRound?.round ?? 0;
    //     newState['totalRound'] = nextLectureRound?.totalRound ?? 0;

    //     if (nextLectureRound.round && nextLectureRound.totalRound) {
    //       if (nextLectureRound.round > nextLectureRound.totalRound) {
    //         setRoundStatus('expiration');
    //         setTotalRoundLength(100);

    //         setSelectedLecture({
    //           round: nextLectureRound.round,
    //           totalRound: nextLectureRound.totalRound
    //         });

    //         return;
    //       } else {
    //         setRoundStatus('available');
    //       }
    //     }
    //   } else {
    //     setRoundStatus('none');
    //   }

    //   const newRound = newState?.round ?? selectedLecture.round;
    //   const newTotalRound = newState?.totalRound ?? selectedLecture.totalRound;
    //   setTotalRoundLength(newTotalRound);

    //   setSelectedLecture({
    //     round: newRound,
    //     totalRound: newTotalRound
    //   });
    // }, [nextLectureRound, isModify]);

    // 수정하는 경우 이미 있는 회차 정보 가져와서 세팅하기
    useEffect(() => {
      if (schedule && isModify) {
        const taughtAt = dayjs(schedule.taughtAt);
        const endedAt = dayjs(schedule.endedAt);

        setSelectedLecture({
          personId: schedule.personId,
          personName: schedule.personName,
          lectureId: schedule.lectureId,
          lectureName:
            lectureList?.find(item => item.id === schedule.lectureId)?.title ??
            '',
          round: schedule.round,
          activeDate: taughtAt,
          startTime: taughtAt.format('HH:mm'),
          endedTime: endedAt.format('HH:mm'),
          totalRound: schedule.totalRound
        });
      }
    }, [schedule, lectureList, lectureScheduleId]);

    useImperativeHandle(ref, () => ({
      handleSave
    }));

    if (isLoading && isModify) {
      return <Loading />;
    }

    return (
      <S.Container>
        <CoachMark.SpotLight
          style={{
            padding: '12px',
            borderRadius: '12px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}
        >
          <CoachMark.Tooltip
            message={`회원과 수강권을 선택하면 자동으로 \n 회차가 계산되어 쉽게 수업 일정을 잡을 수 있어요`}
            position="bottom"
          />

          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
          >
            <LabelWithButton
              label="회원"
              disabled={isModify}
              defaultButtonText={
                !selectedLecture.personName || isGroup
                  ? '선택'
                  : selectedLecture.personName
              }
              onClick={() =>
                !isModify && navigate(PATH.WEEKLY_CALENDER_SEARCH_CLIENT)
              }
            />
            {isGroup && (
              <div
                style={{
                  width: 'calc(100vw - 40px)',
                  display: 'flex',
                  gap: '8px',
                  justifyContent: 'flex-end',
                  flexWrap: 'wrap',
                  minWidth: 0 // Ensure flex items can shrink
                }}
              >
                {groupMembers.map(member => (
                  <LabelButton
                    key={member.personId}
                    style={{ whiteSpace: 'nowrap', width: 'fit-content' }}
                    disabled={true}
                  >
                    {member.personName}
                  </LabelButton>
                ))}
              </div>
            )}
          </div>

          <Membership
            onClick={() =>
              !isModify && navigate(PATH.WEEKLY_CALENDER_SEARCH_MEMBERSHIP)
            }
          />

          {isGroup ? (
            groupMembers.map((member, index) => {
              const setSelectedLecture = (
                roundInfo: Partial<{
                  personId: string;
                  lectureId: string;
                  round: number;
                  totalRound: number;
                }>
              ) => {
                setGroupMembers(prev => {
                  const newGroupMembers = [...prev];
                  newGroupMembers[index] = {
                    ...newGroupMembers[index],
                    round: roundInfo.round || 0,
                    totalRound: roundInfo.totalRound || 0
                  };
                  return newGroupMembers;
                });
              };

              return (
                <GroupRoundInfo
                  setSelectedLecture={setSelectedLecture}
                  key={member.personId}
                  selectedLecture={{
                    ...member,
                    lectureId: selectedLecture.lectureId
                  }}
                  isModify={isGroupModify}
                  lectureScheduleId={lectureScheduleId}
                />
              );
            })
          ) : (
            <RoundInfo
              isModify={isModify}
              selectedLecture={selectedLecture}
              setSelectedLecture={setSelectedLecture}
              isRoundModify={isRoundModify}
              roundStatus={roundStatus}
              handleTotalRound={handleTotalRound}
              personId={selectedLecture.personId}
            />
          )}
        </CoachMark.SpotLight>

        <S.StyledLine color="#262231" />

        <LabelWithButton
          active={!!isCalenderOpen}
          label={'날짜'}
          defaultButtonText={selectedLecture?.activeDate?.format(
            'MM월DD일 (ddd)'
          )}
          onClick={() => setIsCalenderOpen(prev => !prev)}
        />
        {isCalenderOpen && (
          <FibudCalendarV2
            defaultValue={selectedLecture?.activeDate}
            onChange={date => {
              setSelectedLecture({ activeDate: date });
              setIsCalenderOpen(false);
            }}
          />
        )}

        <S.CountBox>
          <Text>시작</Text>
          <S.DropdownBox>
            <Dropdown
              value={selectedLecture.startTime}
              placeholder=""
              options={startTimeOptions}
              onChange={handleChangeStartTime}
            />
          </S.DropdownBox>
        </S.CountBox>

        <S.CountBox>
          <Text>종료</Text>
          <S.DropdownBox>
            <Dropdown
              value={selectedLecture.endedTime}
              placeholder=""
              onChange={handleChangeEndTime}
              options={endTimeOptions}
            />
          </S.DropdownBox>
        </S.CountBox>
        <S.StyledLine color="#262231" />

        <Repitition isGroup={isGroup} schedule={schedule} />
      </S.Container>
    );
  }
);

export default LectureScheduleContents;
